import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	HookStateFunction,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createStateHook, createActionsHook } from 'react-sweet-state';

type InitialState = {
	firstRedactionIdRendered: string | null;
	titleRedactionRenderedForContentId: string | null;
	activeSpotlightIndex: number | null;
	lastActiveSpotlightIndex: number | null;
};

const initialState: InitialState = {
	firstRedactionIdRendered: null,
	titleRedactionRenderedForContentId: null,
	activeSpotlightIndex: null,
	lastActiveSpotlightIndex: null,
};

const Store = createStore({
	initialState,
	actions: {
		setFirstRedactionIdRendered:
			(redactionId: string): (({ getState, setState }: StoreActionApi<InitialState>) => void) =>
			({ getState, setState }) => {
				const { firstRedactionIdRendered } = getState();
				if (firstRedactionIdRendered) return;

				setState({
					firstRedactionIdRendered: redactionId,
				});
			},
		setTitleRedactionRenderedForContentId:
			(contentId: string | null): (({ setState }: StoreActionApi<InitialState>) => void) =>
			({ setState }) => {
				setState({
					titleRedactionRenderedForContentId: contentId,
				});
			},
		resetTour:
			(): (({ setState }: StoreActionApi<InitialState>) => void) =>
			({ setState }) => {
				setState({
					activeSpotlightIndex: initialState.activeSpotlightIndex,
					lastActiveSpotlightIndex: initialState.lastActiveSpotlightIndex,
				});
			},
		initiateTour:
			(): (({ setState }: StoreActionApi<InitialState>) => void) =>
			({ setState }) => {
				setState({
					activeSpotlightIndex: 0,
				});
			},
		proceedToNext:
			(): (({ setState, getState }: StoreActionApi<InitialState>) => void) =>
			({ setState, getState }) => {
				const { activeSpotlightIndex } = getState();
				if (activeSpotlightIndex === null) return;

				setState({
					activeSpotlightIndex: activeSpotlightIndex + 1,
					lastActiveSpotlightIndex: activeSpotlightIndex,
				});
			},
		concludeTour:
			(): (({ setState, getState }: StoreActionApi<InitialState>) => void) =>
			({ setState, getState }) => {
				const { activeSpotlightIndex } = getState();

				setState({
					activeSpotlightIndex: null,
					lastActiveSpotlightIndex: activeSpotlightIndex,
				});
			},
	},
});

export const useRedactionsTourState: HookFunction<
	InitialState,
	BoundActions<
		InitialState,
		{
			setFirstRedactionIdRendered: (
				redactionId: string,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			setTitleRedactionRenderedForContentId: (
				contentId: string | null,
			) => ({ setState }: StoreActionApi<InitialState>) => void;
			resetTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			initiateTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			proceedToNext: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
			concludeTour: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
		}
	>,
	void
> = createHook(Store);
export const useRedactionsTourActions: HookActionsFunction<
	BoundActions<
		InitialState,
		{
			setFirstRedactionIdRendered: (
				redactionId: string,
			) => ({ getState, setState }: StoreActionApi<InitialState>) => void;
			setTitleRedactionRenderedForContentId: (
				contentId: string | null,
			) => ({ setState }: StoreActionApi<InitialState>) => void;
			resetTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			initiateTour: () => ({ setState }: StoreActionApi<InitialState>) => void;
			proceedToNext: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
			concludeTour: () => ({ setState, getState }: StoreActionApi<InitialState>) => void;
		}
	>
> = createActionsHook(Store);
export const useRedactionsTourIsActive: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.activeSpotlightIndex !== null,
});
